import { Box, Button, Avatar, Pagination, Typography } from '@mui/material';
import CustomContainer from '../../components/CustomContainer';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import StatusChip from '../../components/StatusChip';
import AppCard from '../../components/ui/AppCard';
import SearchField from '../../components/ui/SearchField';
import { DataCell, DataList, DataRow } from '../../components/ui/DataList';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import LoadingComponent from '../../components/LoadingComponent';
import { useMemo, useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { resolveQuoteStatus } from '../../utils/variables';
import { FaPlus, FaRegImage } from 'react-icons/fa6';
import { getQuotes } from '../../helpers/httpRequests/quotes';
import dayjs from 'dayjs';

const metalList = {
    1: 'Ouro 18K',
    2: 'Ouro 10K',
    3: 'Prata 950',
};

function metalLabel(value) {
    return metalList[value] || metalList[Number(value)] || value || '-';
}

const columns = [
    { label: 'Nº', width: '4.75rem' },
    { label: 'Produto', width: 'minmax(12rem, 1.6fr)' },
    { label: 'Metal', width: '6.5rem' },
    { label: 'Data', width: '4.25rem' },
    { label: 'Status', width: '8.25rem' },
];

function formatListDate(value) {
    if (!value) return '-';
    const text = String(value);
    const match = text.match(/^(\d{1,2})[/-](\d{1,2})/);
    if (match) {
        return `${match[1].padStart(2, '0')}/${match[2].padStart(2, '0')}`;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD/MM') : text;
}

export default function Quotes() {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const searchDebounced = useDebouncedValue(search, 300);
    const itemsPerPage = 8;

    const listFilters = {
        page,
        pageSize: itemsPerPage,
        search: searchDebounced,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['getQuotes', listFilters],
        queryFn: () => getQuotes(listFilters),
        staleTime: 1000 * 5,
        placeholderData: keepPreviousData,
    });

    const quotes = useMemo(() => data?.data?.data || [], [data]);
    const pagination = data?.data?.pagination;
    const serverPaged = Boolean(pagination);
    const total = pagination?.total ?? quotes.length;
    const pageCount = Math.max(1, pagination?.totalPages ?? Math.ceil(total / itemsPerPage));
    const pageItems = serverPaged
        ? quotes
        : quotes.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (isLoading) {
        return <LoadingComponent />;
    }

    return (
        <CustomContainer>
            <Box sx={{ my: 4 }}>
                <PageHeader
                    title="Meus orçamentos"
                    crumbs={['Meus orçamentos']}
                    action={
                        <Button
                            component={Link}
                            to="/orcamento-cadatrar"
                            variant="contained"
                            color="secondary"
                            startIcon={<FaPlus size={14} />}
                        >
                            Pedir orçamento
                        </Button>
                    }
                />
                <AppCard>
                    <SearchField
                        placeholder="Buscar por produto, metal ou status…"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        sx={{ mb: 2 }}
                    />

                    {quotes.length === 0 ? (
                        total === 0 && !searchDebounced ? (
                            <EmptyState
                                icon={<FaRegImage />}
                                title="Nenhum orçamento ainda"
                                description="Peça um orçamento e acompanhe a resposta por aqui."
                                actionLabel="Pedir orçamento"
                                actionTo="/orcamento-cadatrar"
                            />
                        ) : (
                            <EmptyState
                                icon={<FaRegImage />}
                                title="Nenhum orçamento encontrado"
                                description="Tente ajustar a busca."
                            />
                        )
                    ) : (
                        <DataList columns={columns} minWidth={640}>
                            {pageItems.map((quote, index) => (
                                <DataRow key={quote.id || index} to={`/orcamento-detalhes/${quote.id}`}>
                                    <DataCell sx={styles.id}>#{quote.id}</DataCell>
                                    <DataCell>
                                        <Box sx={styles.prod}>
                                            <Avatar alt={quote.name} src={quote.image} variant="rounded" sx={styles.avatar}>
                                                <FaRegImage size={14} />
                                            </Avatar>
                                            <Typography component="span" sx={styles.prodName}>{quote.name}</Typography>
                                        </Box>
                                    </DataCell>
                                    <DataCell sx={styles.meta}>{metalLabel(quote.selected_metal)}</DataCell>
                                    <DataCell sx={styles.num}>{formatListDate(quote.created_at)}</DataCell>
                                    <DataCell>
                                        <StatusChip
                                            color={resolveQuoteStatus(quote)?.color}
                                            bg={resolveQuoteStatus(quote)?.bg}
                                            label={resolveQuoteStatus(quote)?.label}
                                        />
                                    </DataCell>
                                </DataRow>
                            ))}
                        </DataList>
                    )}

                    {pageCount > 1 && (
                        <Box sx={styles.paginationBox}>
                            <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} color="primary" />
                        </Box>
                    )}
                </AppCard>
            </Box>
        </CustomContainer>
    );
}

const styles = {
    id: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        fontVariantNumeric: 'tabular-nums',
        color: 'text.secondary',
    },
    prod: {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 0,
    },
    avatar: {
        width: 40,
        height: 40,
        bgcolor: 'background.default',
        color: 'text.secondary',
        flexShrink: 0,
    },
    prodName: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    meta: {
        fontSize: '0.8125rem',
        color: 'text.secondary',
    },
    num: {
        fontVariantNumeric: 'tabular-nums',
        fontSize: '0.8125rem',
        color: 'text.secondary',
    },
    paginationBox: {
        width: '100%',
        pt: 3,
        display: 'flex',
        justifyContent: 'flex-end',
    },
};
